/**
 * AI Assistant Panel Component
 * MCP AI chat interface with real backend integration
 */

import React, { useState, useRef, useEffect } from 'react';
import { Button, notification, Modal } from 'antd';
import {
  CloseOutlined,
  RobotOutlined,
  ThunderboltOutlined,
  BookOutlined,
  ExpandOutlined,
  CompressOutlined,
  DeleteOutlined,
  UpOutlined,
  DownOutlined,
} from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import { useMCPChat, useMCPConfirmAction } from '@/hooks/useMCP';
import { ToolResultsDisplay } from '../mcp/ToolResultsDisplay';
import { ConfirmationDialog } from '../mcp/ConfirmationDialog';
import { LoadingIndicator } from '../mcp/LoadingIndicator';
import { QuickActions } from '../mcp/QuickActions';
import { ContextAwareSuggestions } from '../mcp/ContextAwareSuggestions';
import { ToolsExplorer } from '../mcp/ToolsExplorer';
import { SmartChatInput } from '../mcp/SmartChatInput';
import type {
  MCPChatResponse,
  ConfirmationDetails,
} from '@/api/types/mcp.types';
import { debounce } from '@/utils/debounce';
import {
  saveChatHistory,
  loadChatHistory,
  clearChatHistory,
} from '@/utils/chatHistory';
import './AIAssistantPanel.css';

interface Message {
  id: string;
  type: 'user' | 'ai';
  text: string;
  toolResults?: any[];
  timestamp: Date;
}

interface AIAssistantPanelProps {
  isOpen: boolean;
  onClose: () => void;
  isInline?: boolean; // For use in modern layout
}

export const AIAssistantPanel: React.FC<AIAssistantPanelProps> = ({
  isOpen,
  onClose,
  isInline = false,
}) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [confirmationPending, setConfirmationPending] =
    useState<ConfirmationDetails | null>(null);
  const [showQuickActions, setShowQuickActions] = useState(false);
  const [showToolsExplorer, setShowToolsExplorer] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(true); // Show suggestions by default
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const chatMutation = useMCPChat();
  const confirmMutation = useMCPConfirmAction();

  // Load history on mount
  useEffect(() => {
    const history = loadChatHistory();
    if (history.length > 0) {
      // Convert timestamp strings back to Date objects
      const messagesWithDates = history.map((msg) => ({
        ...msg,
        timestamp: new Date(msg.timestamp),
      }));
      setMessages(messagesWithDates);
    } else {
      // Show welcome message
      setMessages([
        {
          id: '1',
          type: 'ai',
          text: "Hello! I'm your AI assistant. I can help you search leads, create tickets, manage calendar events, and more. Try asking me something!",
          timestamp: new Date(),
        },
      ]);
    }
  }, []);

  // Save history when messages change
  useEffect(() => {
    if (messages.length > 1) {
      // Don't save just the welcome message
      saveChatHistory(messages);
    }
  }, [messages]);

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Debounced send function
  const sendMessage = debounce(async () => {
    if (!inputValue.trim() || chatMutation.isPending) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      text: inputValue,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    const messageText = inputValue;
    setInputValue('');

    try {
      const response = await chatMutation.mutateAsync({ message: messageText });
      handleChatResponse(response);
    } catch (error: any) {
      handleError(error);
    }
  }, 1000);

  const handleChatResponse = (response: MCPChatResponse) => {
    if (response.requires_confirmation && response.confirmation_details) {
      // Show confirmation dialog
      setConfirmationPending(response.confirmation_details);

      // Add AI message asking for confirmation
      const aiMessage: Message = {
        id: Date.now().toString() + '_ai',
        type: 'ai',
        text: response.confirmation_details.confirmation_message,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, aiMessage]);
    } else {
      // Add AI response with tool results
      // If tool results exist, show brief summary instead of formatted_message
      let aiText = response.ai_response || 'Action completed successfully.';

      // If we have tool results, extract a brief summary (first line only)
      if (response.tool_results && response.tool_results.length > 0 && aiText) {
        const firstLine = aiText.split('\n')[0];
        // Use first line if it's a summary, otherwise use default message
        aiText = firstLine.includes('**') ? firstLine : '✓ Rezultati pronađeni';
      }

      const aiMessage: Message = {
        id: Date.now().toString() + '_ai',
        type: 'ai',
        text: aiText,
        toolResults: response.tool_results,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, aiMessage]);
    }
  };

  const handleConfirmation = async (confirmed: boolean) => {
    if (!confirmationPending) return;

    try {
      const response = await confirmMutation.mutateAsync({
        toolName: confirmationPending.tool_name,
        toolArgs: confirmationPending.tool_args,
        confirmed,
      });

      const message: Message = {
        id: Date.now().toString(),
        type: 'ai',
        text: confirmed
          ? `Action executed: ${response.result?.message || 'Success'}`
          : 'Action cancelled',
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, message]);
    } catch (error: any) {
      handleError(error);
    } finally {
      setConfirmationPending(null);
    }
  };

  const handleError = (error: any) => {
    const status = error.response?.status;
    const detail = error.response?.data?.detail;

    let errorMessage = 'An error occurred. Please try again.';

    if (status === 401) {
      errorMessage = 'Session expired. Please log in again.';
      // Redirect to login handled by apiClient interceptor
    } else if (status === 503) {
      errorMessage =
        'AI service is currently unavailable. Please try again later.';
    } else if (status === 422) {
      // Validation error
      errorMessage = 'Invalid input. Please check your message and try again.';
    } else if (detail) {
      errorMessage = typeof detail === 'string' ? detail : 'Request failed';
    }

    notification.error({
      message: 'Error',
      description: errorMessage,
    });

    const errorMsg: Message = {
      id: Date.now().toString(),
      type: 'ai',
      text: errorMessage,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, errorMsg]);
  };

  const handleQuerySelect = (query: string) => {
    setInputValue(query);
    setShowQuickActions(false);
    // Auto-send the query after a short delay
    setTimeout(() => {
      if (query.trim()) {
        sendMessage();
      }
    }, 300);
  };

  const handleToolSelect = (toolName: string, _toolDescription: string) => {
    // Populate input with a template query using the tool
    const template = `How do I use ${toolName}?`;
    setInputValue(template);
    setShowToolsExplorer(false);
  };

  const handleClearChat = () => {
    Modal.confirm({
      title: 'Obriši chat?',
      content:
        'Da li sigurno želiš da obrišeš svu istoriju chata? Ova akcija se ne može poništiti.',
      okText: 'Obriši',
      cancelText: 'Otkaži',
      okButtonProps: { danger: true },
      onOk: () => {
        clearChatHistory();
        setMessages([
          {
            id: '1',
            type: 'ai',
            text: "Hello! I'm your AI assistant. I can help you search leads, create tickets, manage calendar events, and more. Try asking me something!",
            timestamp: new Date(),
          },
        ]);
        notification.success({
          message: 'Chat obrisan',
          description: 'Istorija chata je uspešno obrisana.',
          duration: 2,
        });
      },
    });
  };

  const classList = [
    'ai-assistant-panel',
    isInline && 'ai-assistant-inline',
    isExpanded && 'ai-assistant-expanded',
    isOpen ? 'ai-assistant-open' : 'ai-assistant-closed',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={classList}>
      {/* Header */}
      <div className="ai-assistant-header">
        <div className="ai-assistant-header-title">
          <RobotOutlined className="ai-assistant-icon" />
          <h3>AI Assistant</h3>
        </div>
        <div className="ai-assistant-header-actions">
          <Button
            type={showQuickActions ? 'primary' : 'text'}
            icon={<ThunderboltOutlined />}
            onClick={() => {
              setShowQuickActions(!showQuickActions);
              setShowToolsExplorer(false);
            }}
            title="Brzi upiti"
            className="ai-assistant-quick-toggle"
          />
          <Button
            type={showToolsExplorer ? 'primary' : 'text'}
            icon={<BookOutlined />}
            onClick={() => {
              setShowToolsExplorer(!showToolsExplorer);
              setShowQuickActions(false);
            }}
            title="Pregled alata"
            className="ai-assistant-tools-toggle"
          />
          <Button
            type="text"
            icon={<DeleteOutlined />}
            onClick={handleClearChat}
            title="Obriši chat"
            className="ai-assistant-clear-toggle"
            danger
          />
          <Button
            type="text"
            icon={isExpanded ? <CompressOutlined /> : <ExpandOutlined />}
            onClick={() => setIsExpanded(!isExpanded)}
            title={isExpanded ? 'Skupi' : 'Raširi'}
            className="ai-assistant-expand-toggle"
          />
          {!isInline && (
            <Button
              type="text"
              icon={<CloseOutlined />}
              onClick={onClose}
              title="Zatvori"
              className="ai-assistant-close"
            />
          )}
        </div>
      </div>

      {/* Quick Actions (overlay) */}
      {showQuickActions && (
        <QuickActions
          onSelect={handleQuerySelect}
          onClose={() => setShowQuickActions(false)}
        />
      )}

      {/* Tools Explorer (overlay) */}
      {showToolsExplorer && (
        <div className="tools-explorer-overlay">
          <ToolsExplorer onToolSelect={handleToolSelect} />
          <Button
            type="primary"
            onClick={() => setShowToolsExplorer(false)}
            style={{ marginTop: '12px', width: '100%' }}
          >
            Close
          </Button>
        </div>
      )}

      {/* Messages */}
      <div className="ai-assistant-messages">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`ai-message ${
              message.type === 'user' ? 'ai-message-user' : 'ai-message-ai'
            }`}
          >
            <div className="ai-message-content">
              {message.type === 'ai' ? (
                <ReactMarkdown>{message.text}</ReactMarkdown>
              ) : (
                <span style={{ whiteSpace: 'pre-wrap' }}>{message.text}</span>
              )}
            </div>

            {/* Tool Results */}
            {message.toolResults && message.toolResults.length > 0 && (
              <ToolResultsDisplay results={message.toolResults} />
            )}

            <div className="ai-message-timestamp">
              {message.timestamp instanceof Date
                ? message.timestamp.toLocaleTimeString()
                : new Date(message.timestamp).toLocaleTimeString()}
            </div>
          </div>
        ))}

        {chatMutation.isPending && <LoadingIndicator />}

        <div ref={messagesEndRef} />
      </div>

      {/* Confirmation Dialog */}
      {confirmationPending && (
        <ConfirmationDialog
          details={confirmationPending}
          onConfirm={() => handleConfirmation(true)}
          onCancel={() => handleConfirmation(false)}
          loading={confirmMutation.isPending}
        />
      )}

      {/* Input Area */}
      <div className="ai-assistant-input-container">
        {/* Context-Aware Suggestions - Collapsible */}
        {!showQuickActions && !showToolsExplorer && showSuggestions && (
          <div className="suggestions-wrapper">
            <ContextAwareSuggestions onSelect={handleQuerySelect} />
          </div>
        )}

        {/* Suggestions Toggle Button */}
        {!showQuickActions && !showToolsExplorer && (
          <div className="suggestions-toggle-wrapper">
            <Button
              type="text"
              size="small"
              icon={showSuggestions ? <UpOutlined /> : <DownOutlined />}
              onClick={() => setShowSuggestions(!showSuggestions)}
              className="suggestions-toggle-btn"
            >
              {showSuggestions ? 'Sakrij sugestije' : 'Prikaži sugestije'}
            </Button>
          </div>
        )}

        {/* Smart Input */}
        <div className="ai-assistant-input">
          <SmartChatInput
            value={inputValue}
            onChange={setInputValue}
            onSend={sendMessage}
            disabled={chatMutation.isPending || confirmMutation.isPending}
            placeholder="Ask me anything..."
          />
        </div>
      </div>
    </div>
  );
};
